"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  MoneyReceive01Icon,
  GoalIcon,
  Wallet01Icon,
  Alert01Icon,
  Calendar01Icon,
  ChartIncreaseIcon,
  ArrowDown01Icon,
  Tick02Icon,
  Download01Icon,
  Loading03Icon,
} from "@hugeicons/core-free-icons";
import PageHeader from "@/components/layout/PageHeader";
import { notify } from "@/components/ui/dialogs";
import { get, getBlob } from "../utils/service";

interface MonthPoint {
  month: string;
  label: string;
  expected: string;
  collected: string;
  disbursed: string;
}

interface RecentPayment {
  id: string;
  loan_id: string;
  client_name: string | null;
  amount: string;
  date: string;
}

export interface RevenueData {
  currency: string;
  range: {
    type: "preset" | "custom";
    months: number | null;
    label: string;
    from: string;
    to: string;
    granularity: "day" | "month";
  };
  summary: {
    collected: string;
    expected: string;
    disbursed: string;
    outstanding: string;
    overdue: string;
    due_next_30_days: string;
    collection_rate: number | null;
  };
  monthly: MonthPoint[];
  recent_payments: RecentPayment[];
}

type Period = { kind: "preset"; months: number } | { kind: "custom"; from: string; to: string };

const PRESETS = [3, 6, 12, 24];
const MAX_CUSTOM_DAYS = 1830; // matches the server: about five years

const toYmd = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const parseYmd = (ymd: string) => new Date(`${ymd}T00:00:00`);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "1 Sep 2026", the same everywhere and the same as the PDF (browsers disagree on "Sep" vs "Sept").
const shortDate = (ymd: string) => {
  const [year, month, day] = ymd.slice(0, 10).split("-").map(Number);
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

const daysBetween = (from: string, to: string) =>
  Math.round((parseYmd(to).getTime() - parseYmd(from).getTime()) / 86_400_000);

const periodLabel = (period: Period) =>
  period.kind === "preset" ? `Last ${period.months} months` : `${shortDate(period.from)} to ${shortDate(period.to)}`;

const periodQuery = (period: Period) =>
  period.kind === "preset" ? `months=${period.months}` : `from=${period.from}&to=${period.to}`;

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// recharts draws SVG, which cannot use Tailwind classes, so the chart colours are the brand
// values from tailwind.config.js (primary navy, secondary gold) plus Tailwind's gray-200 / gray-500.
const NAVY = "#011C38";
const GOLD = "#E7AA50";
const GRID = "#e5e7eb";
const AXIS = "#6b7280";

const money = (value: string | number, currency = "UGX") => {
  const n = typeof value === "string" ? parseFloat(value) : value;
  return `${currency} ${(isNaN(n) ? 0 : n).toLocaleString()}`;
};

const formatDate = shortDate;

const Card = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`rounded-3xl border border-gray-200 bg-white p-4 sm:p-5 ${className}`}>{children}</div>
);

function PeriodSelect({ period, onChange }: { period: Period; onChange: (period: Period) => void }) {
  const today = toYmd(new Date());
  const [open, setOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(period.kind === "custom");
  const [from, setFrom] = useState(period.kind === "custom" ? period.from : toYmd(new Date(Date.now() - 29 * 86_400_000)));
  const [to, setTo] = useState(period.kind === "custom" ? period.to : today);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const customError = !from || !to
    ? "Choose both dates."
    : from > to
      ? "The start date must be on or before the end date."
      : daysBetween(from, to) > MAX_CUSTOM_DAYS
        ? "Choose a range of five years or less."
        : null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-800 transition-colors hover:bg-gray-50"
      >
        <HugeiconsIcon icon={Calendar01Icon} size={15} className="text-gray-500" />
        {periodLabel(period)}
        <HugeiconsIcon icon={ArrowDown01Icon} size={14} className={`text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div role="listbox" aria-label="Period" className="absolute left-0 top-full z-40 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-2xl border border-gray-200 bg-white p-2 shadow-xl">
          {PRESETS.map((months) => {
            const selected = period.kind === "preset" && period.months === months;
            return (
              <button
                key={months}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange({ kind: "preset", months });
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs transition-colors hover:bg-gray-50 ${selected ? "font-semibold text-gray-900" : "text-gray-700"}`}
              >
                Last {months} months
                {selected && <HugeiconsIcon icon={Tick02Icon} size={14} className="text-primary" />}
              </button>
            );
          })}

          <div className="my-1 border-t border-gray-100" />

          <button
            type="button"
            role="option"
            aria-selected={period.kind === "custom"}
            onClick={() => setShowCustom((value) => !value)}
            className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-xs transition-colors hover:bg-gray-50 ${period.kind === "custom" ? "font-semibold text-gray-900" : "text-gray-700"}`}
          >
            Custom range
            <HugeiconsIcon icon={period.kind === "custom" ? Tick02Icon : ArrowDown01Icon} size={14} className={period.kind === "custom" ? "text-primary" : `text-gray-400 transition-transform ${showCustom ? "rotate-180" : ""}`} />
          </button>

          {showCustom && (
            <div className="space-y-3 px-3 pb-2 pt-2">
              <label className="block text-[11px] font-medium text-gray-500">
                From
                <input
                  type="date"
                  value={from}
                  max={to || undefined}
                  onChange={(event) => setFrom(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </label>
              <label className="block text-[11px] font-medium text-gray-500">
                To
                <input
                  type="date"
                  value={to}
                  min={from || undefined}
                  onChange={(event) => setTo(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </label>
              {customError && <p className="text-[11px] text-red-600">{customError}</p>}
              <button
                type="button"
                disabled={!!customError}
                onClick={() => {
                  onChange({ kind: "custom", from, to });
                  setOpen(false);
                }}
                className="w-full rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Apply dates
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  caption,
  iconClass,
  children,
}: {
  icon: typeof MoneyReceive01Icon;
  label: string;
  value: string;
  caption: string;
  iconClass: string;
  children?: React.ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-center gap-2.5">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconClass}`}>
          <HugeiconsIcon icon={icon} size={18} />
        </span>
        <p className="text-xs font-medium text-gray-500">{label}</p>
      </div>
      <p className="mt-3 break-words text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">{value}</p>
      <p className="mt-0.5 text-xs text-gray-500">{caption}</p>
      {children}
    </Card>
  );
}

function RevenueTooltip({
  active,
  payload,
  label,
  currency,
  suffix = "",
}: {
  active?: boolean;
  payload?: Array<{ payload: { expected?: number; collected?: number } }>;
  label?: string;
  currency: string;
  suffix?: string;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="min-w-[13rem] rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-xs shadow-lg">
      <p className="mb-1.5 font-semibold text-gray-900">{label}</p>
      <p className="flex items-center gap-2 text-gray-600">
        <span className="h-2 w-2 rounded-full bg-primary" />
        Expected{suffix}
        <span className="ml-auto font-semibold text-gray-900">{money(point.expected ?? 0, currency)}</span>
      </p>
      <p className="mt-1 flex items-center gap-2 text-gray-600">
        <span className="h-2 w-2 rounded-full bg-secondary" />
        Collected{suffix}
        <span className="ml-auto font-semibold text-gray-900">{money(point.collected ?? 0, currency)}</span>
      </p>
    </div>
  );
}

const Legend = () => (
  <div className="flex items-center gap-4 text-xs text-gray-600">
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm bg-primary" /> Expected
    </span>
    <span className="flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-sm bg-secondary" /> Collected
    </span>
  </div>
);

const NoActivity = () => (
  <div className="flex h-full flex-col items-center justify-center text-center">
    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400">
      <HugeiconsIcon icon={ChartIncreaseIcon} size={22} />
    </span>
    <p className="mt-3 text-sm font-semibold text-gray-900">No revenue activity in this period</p>
    <p className="mt-1 text-xs text-gray-500">Loans falling due and payments received will show up here.</p>
  </div>
);

export function RevenueView({ data, refreshing = false }: { data: RevenueData; refreshing?: boolean }) {
  const navigate = useNavigate();
  const { currency, summary } = data;

  const chartData = useMemo(
    () =>
      data.monthly.map((point) => ({
        label: point.label,
        expected: Number(point.expected),
        collected: Number(point.collected),
      })),
    [data.monthly]
  );

  const cumulative = useMemo(() => {
    let expected = 0;
    let collected = 0;
    return chartData.map((point) => {
      expected += point.expected;
      collected += point.collected;
      return { label: point.label, expected, collected };
    });
  }, [chartData]);

  const hasActivity = chartData.some((point) => point.expected > 0 || point.collected > 0);
  const rate = summary.collection_rate;

  return (
    <div className={`space-y-5 transition-opacity ${refreshing ? "opacity-60" : ""}`}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Kpi
          icon={MoneyReceive01Icon}
          label="Collected"
          value={money(summary.collected, currency)}
          caption={rate === null ? "Payments received in this period" : `${rate}% of the ${money(summary.expected, currency)} expected`}
          iconClass="bg-secondary/20 text-secondary-scale-700"
        >
          {rate !== null && (
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
              <div className="h-full rounded-full bg-secondary" style={{ width: `${Math.min(Math.max(rate, 0), 100)}%` }} />
            </div>
          )}
        </Kpi>
        <Kpi
          icon={GoalIcon}
          label="Expected"
          value={money(summary.expected, currency)}
          caption="Falling due in this period, including overdue charges"
          iconClass="bg-primary/10 text-primary"
        />
        <Kpi
          icon={Wallet01Icon}
          label="Outstanding"
          value={money(summary.outstanding, currency)}
          caption="Still to be collected on unpaid loans"
          iconClass="bg-gray-100 text-gray-700"
        />
        <Kpi
          icon={Alert01Icon}
          label="Overdue"
          value={money(summary.overdue, currency)}
          caption="Past the due date and unpaid"
          iconClass="bg-red-50 text-red-600"
        />
        <Kpi
          icon={Calendar01Icon}
          label="Due in the next 30 days"
          value={money(summary.due_next_30_days, currency)}
          caption="Expected to come in soon"
          iconClass="bg-amber-50 text-amber-600"
        />
        <Kpi
          icon={ChartIncreaseIcon}
          label="Disbursed"
          value={money(summary.disbursed, currency)}
          caption="Principal lent out in this period"
          iconClass="bg-gray-100 text-gray-700"
        />
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Expected vs collected</h2>
            <p className="text-xs text-gray-500">By {data.range.granularity}: what fell due and what was actually paid</p>
          </div>
          <Legend />
        </div>
        <div className="mt-4 h-72 sm:h-80">
          {hasActivity ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={GRID} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS }} interval="preserveStartEnd" minTickGap={8} />
                <YAxis tickLine={false} axisLine={false} width={88} tick={{ fontSize: 11, fill: AXIS }} tickFormatter={(value: number) => value.toLocaleString()} />
                <Tooltip content={<RevenueTooltip currency={currency} />} cursor={{ fill: "rgba(1, 28, 56, 0.05)" }} />
                <Bar dataKey="expected" name="Expected" fill={NAVY} radius={[6, 6, 0, 0]} maxBarSize={28} />
                <Bar dataKey="collected" name="Collected" fill={GOLD} radius={[6, 6, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <NoActivity />
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <Card className="min-w-0 xl:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Running total</h2>
              <p className="text-xs text-gray-500">Cumulative expected against cumulative collected</p>
            </div>
            <Legend />
          </div>
          <div className="mt-4 h-64 sm:h-72">
            {hasActivity ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cumulative} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="fillExpected" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={NAVY} stopOpacity={0.18} />
                      <stop offset="100%" stopColor={NAVY} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="fillCollected" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GOLD} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={GRID} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: AXIS }} interval="preserveStartEnd" minTickGap={8} />
                  <YAxis tickLine={false} axisLine={false} width={88} tick={{ fontSize: 11, fill: AXIS }} tickFormatter={(value: number) => value.toLocaleString()} />
                  <Tooltip content={<RevenueTooltip currency={currency} suffix=" (total)" />} />
                  <Area type="monotone" dataKey="expected" name="Expected" stroke={NAVY} strokeWidth={2} fill="url(#fillExpected)" />
                  <Area type="monotone" dataKey="collected" name="Collected" stroke={GOLD} strokeWidth={2} fill="url(#fillCollected)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <NoActivity />
            )}
          </div>
        </Card>

        <Card className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">Recent payments</h2>
          <p className="text-xs text-gray-500">The latest money received</p>
          <div className="mt-3 space-y-1">
            {data.recent_payments.length === 0 ? (
              <p className="py-8 text-center text-xs text-gray-500">No payments recorded yet.</p>
            ) : (
              data.recent_payments.map((payment) => (
                <button
                  key={payment.id}
                  type="button"
                  onClick={() => navigate(`/loans?highlight=${payment.loan_id}`)}
                  className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
                    {(payment.client_name || "?").charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{payment.client_name || "Unknown client"}</p>
                    <p className="text-[11px] text-gray-500">{formatDate(payment.date)}</p>
                  </div>
                  <p className="shrink-0 text-sm font-semibold text-green-600">+{money(payment.amount, currency)}</p>
                </button>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

const Skeleton = () => (
  <div className="space-y-5 animate-pulse">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-32 rounded-3xl bg-gray-100" />
      ))}
    </div>
    <div className="h-96 rounded-3xl bg-gray-100" />
    <div className="h-80 rounded-3xl bg-gray-100" />
  </div>
);

export default function RevenuePage() {
  const [period, setPeriod] = useState<Period>({ kind: "preset", months: 12 });
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const query = periodQuery(period);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    get<{ success: boolean; data: RevenueData }>(`/revenue?${query}`)
      .then((response) => {
        if (cancelled) return;
        if (response.success) setData(response.data);
        else setError(true);
      })
      .catch((err) => {
        console.error("Error loading revenue:", err);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, reloadKey]);

  const handleExport = async () => {
    if (!data || exporting) return;
    setExporting(true);
    try {
      const blob = await getBlob(`/revenue/export?${query}`);
      if (!blob.type.includes("pdf")) throw new Error("The server did not return a PDF");
      downloadBlob(blob, `revenue-report_${data.range.from}_to_${data.range.to}.pdf`);
    } catch (err) {
      console.error("Error exporting revenue report:", err);
      notify("Could not create the PDF report. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen p-4 pb-8 sm:p-0 sm:pb-8">
      <PageHeader title="Revenue" description="Money collected, expected and still to come in" />

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <PeriodSelect period={period} onChange={setPeriod} />
        <button
          type="button"
          onClick={handleExport}
          disabled={!data || loading || exporting}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <HugeiconsIcon icon={exporting ? Loading03Icon : Download01Icon} size={15} className={exporting ? "animate-spin" : ""} />
          {exporting ? "Preparing PDF..." : "Export PDF"}
        </button>
      </div>

      {data ? (
        <RevenueView data={data} refreshing={loading} />
      ) : error ? (
        <Card className="text-center">
          <p className="text-sm font-semibold text-gray-900">Could not load revenue</p>
          <p className="mt-1 text-xs text-gray-500">Check your connection and try again.</p>
          <button
            type="button"
            onClick={() => setReloadKey((key) => key + 1)}
            className="mt-4 rounded-full bg-primary px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
        </Card>
      ) : (
        <Skeleton />
      )}
    </div>
  );
}
